import { useSession } from '@side/next-auth/react';
import { toast } from '@side/pantry';

import { useState } from 'react';
import { useWatch } from 'react-hook-form';
import { coreApi } from 'src/modules/api-clients/clients';
import { useMutateInteractionLists } from '@/common/hooks/useMutateInteractionLists';
import { sanitizeFilename } from '@/common/utils/sanitizeFilename';
import { useAddPageBannerToState } from '@/state/navigation';
import {
  MLSBanner,
  MLSBannerId,
} from '@/views/Coversheet/components/PageBanner/MLSBanner/MLSBanner';
import { useCreateOfferContext } from '../context/CreateOfferContext';
import { BuyerMethod, type CreateOfferFormData } from '../types';

export const useBuyerStepSubmit = () => {
  const values = useWatch<CreateOfferFormData>();
  const [isLoading, setIsLoading] = useState(false);
  const session = useSession();
  const addBanner = useAddPageBannerToState();
  const mutateInteractionLists = useMutateInteractionLists();
  const { sourceId } = useCreateOfferContext();
  const owner = session.data?.user?.id;

  if (!owner) {
    throw new Error('Invalid session');
  }

  const createNewBuyer = async () => {
    const { buyerAgents, newBuyerClients } = values;

    // Filter out undefined/empty agents
    const validAgents = (buyerAgents ?? []).filter((agent) => agent?.id);

    if (!validAgents.length) {
      throw new Error('At least one agent is required');
    }

    // First agent is the primary agent (owner)
    const primaryAgent = validAgents[0];
    // Other agents are added as contacts
    const additionalAgents = validAgents.slice(1);

    const response = await coreApi.POST('/client-groups', {
      body: {
        // Owner = Primary Agent
        owner: primaryAgent.userIdentityId,
        teamId: primaryAgent.teamIdentityId,
        // The backend assigns contact order on creation
        contacts: [
          ...additionalAgents.map((agent) => ({
            userId: agent.userIdentityId,
            teamId: agent.teamIdentityId,
            firstName: agent.firstName ?? '',
            lastName: agent.lastName ?? '',
            email: agent.email ?? '',
            type: 'agent' as const,
          })),
          ...(newBuyerClients ?? []).map((client) => ({
            ...client,
            type: 'client' as const,
          })),
        ],
      },
    });

    return response.data;
  };

  const deletePriorSignedAgreement = async (interactionId: string) => {
    const attachments = await coreApi.GET(
      `/interactions/{interactionId}/attachments`,
      {
        params: {
          path: {
            interactionId,
          },
        },
      },
    );
    const attachmentId = attachments.data?.find(
      (attachment) => attachment.type === 'signedAgreement',
    )?.id;
    if (!attachmentId) {
      return;
    }
    await coreApi.DELETE(
      `/interactions/{interactionId}/attachments/{attachmentId}`,
      {
        params: {
          path: {
            interactionId,
            attachmentId,
          },
        },
      },
    );
  };

  const uploadSignedAgreement = async (interactionId: string, file: File) => {
    await deletePriorSignedAgreement(interactionId);
    const response = await coreApi.POST(
      `/interactions/{interactionId}/attachments/upload`,
      {
        params: {
          path: {
            interactionId,
          },
        },
        body: {
          names: [sanitizeFilename(file.name)],
          type: 'signedAgreement',
        },
      },
    );
    const { attachment, uploadUrl } = response.data?.[0] ?? {};

    if (!attachment || !uploadUrl) {
      throw new Error('Failed to upload signed agreement');
    }

    const fileId = attachment.file?.id?.toString() ?? '';
    const matchedFile = file;

    const uploadResponse = await fetch(uploadUrl, {
      method: 'PUT',
      body: matchedFile,
      headers: {
        'Content-Type': matchedFile.type,
        'x-goog-meta-file-id': fileId,
      },
    });
    if (!uploadResponse.ok) {
      throw new Error('Failed to upload signed agreement');
    }
  };

  const property = {
    street1: values.street1,
    street2: values.street2,
    city: values.city,
    state: values.state,
    zip: values.zip,
    county: values.county,
    mlsNumber: values.mlsNumber,
    mlsServiceId: values.mlsServiceId,
  };

  const maybeAddMlsBanner = (offerId: string) => {
    if (values.mlsNumber) {
      addBanner(
        { id: MLSBannerId, banner: <MLSBanner interactionId={offerId} /> },
        offerId,
      );
    }
  };

  const createOffer = async (clientGroupId: string) => {
    const response = await coreApi.POST('/offers', {
      body: {
        clientGroupId,
        owner,
        status: 'active' as const,
        ...(values.listingId
          ? { listingId: values.listingId }
          : {
              listing: {
                status: 'active' as const,
                owner,
                property,
              },
            }),
      },
    });

    if (response.error === undefined) {
      maybeAddMlsBanner(response.data.id);
    }

    return response.data;
  };

  // Unrepresented buyer: no client-group is created. The offer carries the
  // selected seller agent as a counter-party agent contact and each buyer as
  // a client contact.
  const buildUnrepresentedContacts = () => {
    const { sellerAgent, newBuyerClients } = values;

    if (!sellerAgent?.userIdentityId) {
      throw new Error('Seller agent is required');
    }

    return [
      {
        type: 'counterpartyAgent' as const,
        firstName: sellerAgent.firstName,
        lastName: sellerAgent.lastName,
        email: sellerAgent.email,
        userId: sellerAgent.userIdentityId,
        teamId: sellerAgent.teamIdentityId,
      },
      ...(newBuyerClients ?? []).map((client) => ({
        ...client,
        type: 'client' as const,
      })),
    ];
  };

  const createUnrepresentedOffer = async () => {
    const contacts = buildUnrepresentedContacts();

    const response = await coreApi.POST('/offers', {
      body: {
        status: 'active' as const,
        hasUnrepresentedClient: true,
        ...(values.listingId
          ? { listingId: values.listingId }
          : {
              listing: {
                status: 'active' as const,
                property,
              },
            }),
        contacts,
      },
    });

    if (response.error === undefined) {
      maybeAddMlsBanner(response.data.id);
    }

    return response.data;
  };

  // The offer clone endpoint copies every sub-resource (documents, envelopes,
  // contacts, price history, cover photo) from the source offer atomically,
  // attaching the buyer (client group or unrepresented-buyer contacts) in the
  // same request. Property overrides are flat in the body.
  const cloneOffer = async (
    buyer:
      | { clientGroupId: string }
      | {
          contacts: ReturnType<typeof buildUnrepresentedContacts>;
          hasUnrepresentedClient: true;
        },
  ) => {
    if (!sourceId) {
      throw new Error('Source offer is required');
    }

    const { data, error } = await coreApi.POST('/offers/{offerId}', {
      params: { path: { offerId: sourceId } },
      body: {
        ...property,
        ...buyer,
      },
    });

    if (error !== undefined) {
      throw new Error('Failed to clone offer', { cause: error });
    }

    maybeAddMlsBanner(data.id);

    return data;
  };

  const onSubmit = async () => {
    try {
      setIsLoading(true);

      // Unrepresented buyers don't create a buyer client-group; the seller
      // agent and buyer are added as offer contacts instead.
      if (values.buyerMethod === BuyerMethod.unrepresentedBuyer) {
        const unrepresentedOffer = sourceId
          ? await cloneOffer({
              contacts: buildUnrepresentedContacts(),
              hasUnrepresentedClient: true,
            })
          : await createUnrepresentedOffer();
        void mutateInteractionLists('offer');
        setIsLoading(false);
        return unrepresentedOffer;
      }

      let clientGroupId: string | undefined;
      if (values.buyerMethod === BuyerMethod.newBuyer) {
        clientGroupId = (await createNewBuyer())?.id;
      } else {
        clientGroupId = values.buyer?.id;
      }

      if (!clientGroupId) {
        throw new Error('Client group ID is required');
      }

      if (values.type === 'attachment') {
        if (values.attachment?.length) {
          await uploadSignedAgreement(clientGroupId, values.attachment[0]);
        }
      }
      const offer = sourceId
        ? await cloneOffer({ clientGroupId })
        : await createOffer(clientGroupId);

      void mutateInteractionLists('offer');
      setIsLoading(false);
      return offer;
    } catch (error: unknown) {
      toast('error', (error as Error)?.message ?? 'Failed to create offer');
      setIsLoading(false);
      return undefined;
    }
  };

  return {
    onSubmit,
    isLoading,
  };
};
